/**
 * ReplicationBinaryCodec - low-level binary encode/decode for replication payloads.
 *
 * Provides BinaryWriter, BinaryReader, a FNV-1a class hash, and helpers for
 * writing/reading class discriminators used by polymorphic `class`-typed properties.
 *
 * All multi-byte values are written in little-endian order.
 */
import { ClassRegistry } from '../systems/ClassRegistry.js';

// ---------------------------------------------------------------------------
// BinaryWriter
// ---------------------------------------------------------------------------

const INITIAL_CAPACITY = 256;

/**
 * Growable binary write buffer.
 * Call {@link BinaryWriter.getBytes} after all writes to obtain the final `Uint8Array`.
 */
export class BinaryWriter {
  private buffer: ArrayBuffer;
  private view: DataView;
  private _offset: number = 0;

  constructor(initialCapacity = INITIAL_CAPACITY) {
    this.buffer = new ArrayBuffer(initialCapacity);
    this.view = new DataView(this.buffer);
  }

  get offset(): number { return this._offset; }

  private grow(needed: number): void {
    const required = this._offset + needed;
    if (required <= this.buffer.byteLength) return;
    let newSize = this.buffer.byteLength * 2;
    while (newSize < required) newSize *= 2;
    const newBuffer = new ArrayBuffer(newSize);
    new Uint8Array(newBuffer).set(new Uint8Array(this.buffer));
    this.buffer = newBuffer;
    this.view = new DataView(this.buffer);
  }

  writeBool(v: boolean): void {
    this.grow(1);
    this.view.setUint8(this._offset, v ? 1 : 0);
    this._offset += 1;
  }

  writeUint8(v: number): void {
    this.grow(1);
    this.view.setUint8(this._offset, v);
    this._offset += 1;
  }

  writeInt8(v: number): void {
    this.grow(1);
    this.view.setInt8(this._offset, v);
    this._offset += 1;
  }

  writeUint16(v: number): void {
    this.grow(2);
    this.view.setUint16(this._offset, v, true);
    this._offset += 2;
  }

  writeInt16(v: number): void {
    this.grow(2);
    this.view.setInt16(this._offset, v, true);
    this._offset += 2;
  }

  writeInt32(v: number): void {
    this.grow(4);
    this.view.setInt32(this._offset, v, true);
    this._offset += 4;
  }

  writeUint32(v: number): void {
    this.grow(4);
    this.view.setUint32(this._offset, v, true);
    this._offset += 4;
  }

  writeFloat32(v: number): void {
    this.grow(4);
    this.view.setFloat32(this._offset, v, true);
    this._offset += 4;
  }

  writeFloat64(v: number): void {
    this.grow(8);
    this.view.setFloat64(this._offset, v, true);
    this._offset += 8;
  }

  /** Write a UTF-8 string prefixed with a uint16 byte length. */
  writeString(v: string): void {
    const bytes = new TextEncoder().encode(v);
    this.writeUint16(bytes.byteLength);
    this.grow(bytes.byteLength);
    new Uint8Array(this.buffer, this._offset).set(bytes);
    this._offset += bytes.byteLength;
  }

  /**
   * Write an actor ID as raw hex bytes.
   * 0 = null/invalid, 8 = shortUUID, 16 = legacy UUID.
   */
  writeActorId(v: string): void {
    const hex = v.replace(/-/g, '').toLowerCase();
    const byteLength = hex.length / 2;
    if ((byteLength !== 8 && byteLength !== 16) || !/^[0-9a-f]+$/.test(hex)) {
      this.writeUint8(0);
      return;
    }

    this.writeUint8(byteLength);
    this.grow(byteLength);
    for (let i = 0; i < byteLength; i++) {
      this.view.setUint8(this._offset + i, parseInt(hex.slice(i * 2, i * 2 + 2), 16));
    }
    this._offset += byteLength;
  }

  /** Write a `Vector2`-like value as two float32s. */
  writeVector2(v: { x: number; y: number }): void {
    this.writeFloat32(v.x);
    this.writeFloat32(v.y);
  }

  /** Write a `Vector3`-like value as three float32s. */
  writeVector3(v: { x: number; y: number; z: number }): void {
    this.writeFloat32(v.x);
    this.writeFloat32(v.y);
    this.writeFloat32(v.z);
  }

  /** Write a `Vector4`/`Quaternion`-like value as four float32s. */
  writeVector4(v: { x: number; y: number; z: number; w: number }): void {
    this.writeFloat32(v.x);
    this.writeFloat32(v.y);
    this.writeFloat32(v.z);
    this.writeFloat32(v.w);
  }

  /** Write an `Euler`-like value as three float32s (order string is omitted on the wire). */
  writeEuler(v: { x: number; y: number; z: number }): void {
    this.writeFloat32(v.x);
    this.writeFloat32(v.y);
    this.writeFloat32(v.z);
  }

  /**
   * Write a presence byte: 0 = null, 1 = value follows.
   * Always call this before writing the actual value for nullable properties.
   */
  writePresence(present: boolean): void {
    this.writeUint8(present ? 1 : 0);
  }

  /**
   * Write a bitmask indicating which property indices are present.
   * `count` is the total number of properties; `presentIndices` is the set of indices that changed.
   */
  writeBitmask(count: number, presentIndices: Set<number>): void {
    const byteCount = Math.ceil(count / 8);
    this.grow(byteCount);
    for (let i = 0; i < byteCount; i++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const idx = i * 8 + bit;
        if (idx < count && presentIndices.has(idx)) {
          byte |= 1 << bit;
        }
      }
      this.view.setUint8(this._offset + i, byte);
    }
    this._offset += byteCount;
  }

  /**
   * Reserve `ceil(count / 8)` zero bytes for a bitmask and return the byte offset where
   * they were written. Call {@link patchBitmask} with that offset once all changed indices
   * are known.
   */
  reserveBitmask(count: number): number {
    const byteCount = Math.ceil(count / 8);
    this.grow(byteCount);
    for (let i = 0; i < byteCount; i++) this.view.setUint8(this._offset + i, 0);
    const startOffset = this._offset;
    this._offset += byteCount;
    return startOffset;
  }

  /**
   * Overwrite a previously reserved bitmask at `atOffset`.
   * Safe to call after any number of additional writes (buffer may have been reallocated,
   * but `this.view` always reflects the current buffer).
   */
  patchBitmask(atOffset: number, count: number, presentIndices: Set<number>): void {
    const byteCount = Math.ceil(count / 8);
    for (let i = 0; i < byteCount; i++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const idx = i * 8 + bit;
        if (idx < count && presentIndices.has(idx)) byte |= 1 << bit;
      }
      this.view.setUint8(atOffset + i, byte);
    }
  }

  /** Overwrite a single uint8 at a previously written position. */
  patchUint8(atOffset: number, v: number): void {
    this.view.setUint8(atOffset, v);
  }

  /**
   * Reset the write cursor to `offset`, discarding all bytes written after it.
   * Used to roll back speculatively written bytes when a value turns out to be unchanged.
   */
  resetTo(offset: number): void {
    this._offset = offset;
  }

  /** Return a copy of the written bytes. */
  getBytes(): Uint8Array {
    return new Uint8Array(this.buffer, 0, this._offset);
  }
}

// ---------------------------------------------------------------------------
// BinaryReader
// ---------------------------------------------------------------------------

/**
 * Sequential binary read cursor over a `Uint8Array`.
 */
export class BinaryReader {
  private view: DataView;
  private _offset: number;

  constructor(data: Uint8Array, startOffset = 0) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    this._offset = startOffset;
  }

  get offset(): number { return this._offset; }
  get byteLength(): number { return this.view.byteLength; }
  get remaining(): number { return this.view.byteLength - this._offset; }

  readBool(): boolean {
    return this.view.getUint8(this._offset++) !== 0;
  }

  readUint8(): number {
    return this.view.getUint8(this._offset++);
  }

  readInt8(): number {
    return this.view.getInt8(this._offset++);
  }

  readUint16(): number {
    const v = this.view.getUint16(this._offset, true);
    this._offset += 2;
    return v;
  }

  readInt16(): number {
    const v = this.view.getInt16(this._offset, true);
    this._offset += 2;
    return v;
  }

  readInt32(): number {
    const v = this.view.getInt32(this._offset, true);
    this._offset += 4;
    return v;
  }

  readUint32(): number {
    const v = this.view.getUint32(this._offset, true);
    this._offset += 4;
    return v;
  }

  readFloat32(): number {
    const v = this.view.getFloat32(this._offset, true);
    this._offset += 4;
    return v;
  }

  readFloat64(): number {
    const v = this.view.getFloat64(this._offset, true);
    this._offset += 8;
    return v;
  }

  /** Read a UTF-8 string prefixed with a uint16 byte length. */
  readString(): string {
    const byteLength = this.readUint16();
    const bytes = new Uint8Array(this.view.buffer, this.view.byteOffset + this._offset, byteLength);
    this._offset += byteLength;
    return new TextDecoder().decode(bytes);
  }

  /**
   * Read an actor ID written by {@link BinaryWriter.writeActorId}.
   * Returns shortUUIDs as 16 hex chars and legacy UUIDs in canonical dashed form.
   */
  readActorId(): string {
    const byteLength = this.readUint8();
    if (byteLength === 0) return '';
    if (byteLength !== 8 && byteLength !== 16) {
      this._offset += byteLength;
      return '';
    }

    const nibbles: string[] = [];
    for (let i = 0; i < byteLength; i++) {
      const b = this.view.getUint8(this._offset + i);
      nibbles.push(b.toString(16).padStart(2, '0'));
    }
    this._offset += byteLength;
    const h = nibbles.join('');
    if (byteLength === 8) return h;
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  /** Skip an actor ID without decoding. */
  skipActorId(): void {
    this._offset += this.readUint8();
  }

  readVector2(): { x: number; y: number } {
    return { x: this.readFloat32(), y: this.readFloat32() };
  }

  readVector3(): { x: number; y: number; z: number } {
    return { x: this.readFloat32(), y: this.readFloat32(), z: this.readFloat32() };
  }

  readVector4(): { x: number; y: number; z: number; w: number } {
    return { x: this.readFloat32(), y: this.readFloat32(), z: this.readFloat32(), w: this.readFloat32() };
  }

  readEuler(): { x: number; y: number; z: number } {
    return { x: this.readFloat32(), y: this.readFloat32(), z: this.readFloat32() };
  }

  /**
   * Read a presence byte. Returns true if the value follows, false if null.
   */
  readPresence(): boolean {
    return this.readUint8() !== 0;
  }

  /**
   * Return all unread bytes as a `Uint8Array` view (no copy).
   * Useful for slicing the args section out of a decoded RPC payload.
   */
  remainingBytes(): Uint8Array {
    return new Uint8Array(this.view.buffer, this.view.byteOffset + this._offset, this.remaining);
  }

  /**
   * Read a bitmask and return which property indices are present.
   * `count` must match the value passed to {@link BinaryWriter.writeBitmask}.
   */
  readBitmask(count: number): boolean[] {
    const byteCount = Math.ceil(count / 8);
    const result = new Array<boolean>(count).fill(false);
    for (let i = 0; i < byteCount; i++) {
      const byte = this.view.getUint8(this._offset + i);
      for (let bit = 0; bit < 8; bit++) {
        const idx = i * 8 + bit;
        if (idx < count) {
          result[idx] = (byte & (1 << bit)) !== 0;
        }
      }
    }
    this._offset += byteCount;
    return result;
  }
}

// ---------------------------------------------------------------------------
// FNV-1a class hash (for polymorphic class discriminator)
// ---------------------------------------------------------------------------

/**
 * 32-bit FNV-1a hash of a string.
 * Used to encode class names compactly on the wire for polymorphic `class` properties.
 */
export function fnv1a32(str: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // Multiply by FNV prime (32-bit: 0x01000193) using 32-bit unsigned arithmetic
    hash = (Math.imul(hash, 0x01000193) >>> 0);
  }
  return hash;
}

// Lazy map: hash → full registered class name (built once from ClassRegistry)
let classHashMap: Map<number, string> | null = null;

/**
 * Build (or return the cached) hash → class name map from the current ClassRegistry.
 * Called lazily on first polymorphic decode.
 */
export function getClassHashMap(): Map<number, string> {
  if (classHashMap) return classHashMap;
  classHashMap = new Map();
  for (const [fullName] of ClassRegistry.getRegistry()) {
    const hash = fnv1a32(fullName);
    if (classHashMap.has(hash)) {
      console.warn(`[ReplicationBinaryCodec] FNV-1a hash collision: classes "${classHashMap.get(hash)}" and "${fullName}" share hash ${hash}. One will be unreachable via polymorphic replication.`);
    }
    classHashMap.set(hash, fullName);
  }
  return classHashMap;
}

/**
 * Invalidate the class hash cache (call after ClassRegistry changes, e.g. game reload).
 */
export function invalidateClassHashMap(): void {
  classHashMap = null;
}

/**
 * Write a uint32 FNV-1a class discriminator for the given object instance.
 * Writes 0 if the class name cannot be resolved (treated as unknown on decode).
 */
export function writeClassDiscriminator(writer: BinaryWriter, instance: object): void {
  const fullName = ClassRegistry.getClassName(instance);
  if (!fullName) {
    writer.writeUint32(0);
    return;
  }
  writer.writeUint32(fnv1a32(fullName));
}

/**
 * Read a uint32 class discriminator and resolve it to a constructor.
 * Returns `null` if the hash is 0 or cannot be resolved; the caller should log a warning
 * and skip the property.
 */
export function readClassDiscriminator(reader: BinaryReader): (new (...args: any[]) => any) | null {
  const hash = reader.readUint32();
  if (hash === 0) return null;
  const map = getClassHashMap();
  const fullName = map.get(hash);
  if (!fullName) {
    console.warn(`[ReplicationBinaryCodec] Unknown class hash ${hash} in polymorphic replication payload. Skipping property.`);
    return null;
  }
  try {
    return ClassRegistry.getClass(fullName);
  } catch {
    console.warn(`[ReplicationBinaryCodec] Class "${fullName}" (hash ${hash}) not found in ClassRegistry. Skipping property.`);
    return null;
  }
}
